import { BlockOutlined, CheckCircle, RadioButtonChecked, RadioButtonUnchecked } from "@mui/icons-material";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { FONT_SIZE, ICON_SIZE, LETTER_SPACING, MOTION } from "../../../theme/tokens";
import MetaChip from "../Badges/MetaChip";

/**
 * Where one item of the list stands. Deliberately four states, not a boolean: a setup list whose rows
 * are only "done" or "not done" cannot say which one is being worked on right now, and cannot
 * distinguish "not yet" from "cannot".
 */
export type StatusChecklistItemState = "done" | "active" | "pending" | "blocked";

/** `compact` is a live progress trail inside a conversation: caption-sized rows where finished rows recede. */
export enum StatusChecklistDensity {
	Default = "default",
	Compact = "compact",
}

export type StatusChecklistItem = {
	id: string;
	/** Already-translated row label. */
	label: string;
	/** Already-translated supporting line. */
	description?: ReactNode;
	state: StatusChecklistItemState;
	/** Already-translated trailing status word (rendered as a `MetaChip`). Omit for no chip. */
	statusLabel?: string;
	/** Replace the state glyph — for a list whose rows have their own identity (a facility, a feed). */
	icon?: ReactNode;
	dataTestId?: string;
};

type Props = {
	items: StatusChecklistItem[];
	/** Already-translated group heading. Omit for a bare list. */
	title?: string;
	/** Stagger the rows in on mount (house 30–80ms window). Off for a list that re-renders often. */
	stagger?: boolean;
	/** Accessible name for the list when there is no visible `title`. */
	ariaLabel?: string;
	// DEFAULT: `Default`, the settings-sized rows every existing caller renders.
	density?: StatusChecklistDensity;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const COMPACT_LINE_HEIGHT = 1.5;

const DENSITY = {
	[StatusChecklistDensity.Default]: {
		rowGap: 1.25,
		glyphGap: 1.25,
		glyphSize: FONT_SIZE.TITLE_MD,
		glyphBox: { mt: "1px" },
		labelSize: FONT_SIZE.EMPHASIS,
		labelWeight: 600,
		labelLineHeight: undefined,
		isolateLabel: false,
	},
	[StatusChecklistDensity.Compact]: {
		rowGap: 0.75,
		glyphGap: 1,
		glyphSize: ICON_SIZE.MICRO,
		// The glyph box is one label line tall and centres the glyph, so a single-line row is optically level.
		glyphBox: { alignItems: "center", height: `calc(${FONT_SIZE.SECONDARY} * ${COMPACT_LINE_HEIGHT})` },
		labelSize: FONT_SIZE.SECONDARY,
		labelWeight: 500,
		labelLineHeight: COMPACT_LINE_HEIGHT,
		isolateLabel: true,
	},
} as const;

const labelColor = (state: StatusChecklistItemState, density: StatusChecklistDensity): string => {
	// A pending row is a promise, not a fact — it sits back until it becomes one.
	if (state === "pending") return "text.secondary";
	// In a live trail the eye belongs on the row being worked on; finished rows step back.
	if (density === StatusChecklistDensity.Compact && state === "done") return "text.secondary";
	return "text.primary";
};

const STATE_GLYPH: Record<StatusChecklistItemState, ReactNode> = {
	done: <CheckCircle />,
	active: <RadioButtonChecked />,
	pending: <RadioButtonUnchecked />,
	blocked: <BlockOutlined />,
};

const STATE_CHIP_TONE = {
	done: "success",
	active: "primary",
	pending: "neutral",
	blocked: "warning",
} as const;

// Rows fade+lift in together as one list. Transform/opacity only; dropped under reduced motion.
const ROW_ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes statusChecklistRowEnter": {
		from: { opacity: 0, transform: "translateY(4px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `statusChecklistRowEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT} both`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const GLYPH_COLOR: Record<StatusChecklistItemState, (theme: Theme) => string> = {
	done: (theme) => theme.palette.success.main,
	active: (theme) => theme.palette.primary.main,
	blocked: (theme) => theme.palette.warning.main,
	pending: (theme) => theme.palette.text.disabled,
};

type RowProps = { item: StatusChecklistItem; index: number; stagger: boolean; density: StatusChecklistDensity };

const StatusChecklistRow: FC<RowProps> = ({ item, index, stagger, density }) => {
	const metrics = DENSITY[density];
	return (
		<Box
			component="li"
			data-testid={item.dataTestId}
			data-state={item.state}
			sx={[
				stagger ? ROW_ENTRANCE_SX : null,
				{
					display: "flex",
					alignItems: "flex-start",
					gap: metrics.glyphGap,
					minWidth: 0,
					animationDelay: stagger ? `${Math.min(index * MOTION.STAGGER.STEP, MOTION.STAGGER.MAX)}ms` : undefined,
				},
			]}
		>
			<Box
				aria-hidden
				sx={(theme: Theme) => ({
					display: "flex",
					flexShrink: 0,
					...metrics.glyphBox,
					color: GLYPH_COLOR[item.state](theme),
					"& > svg": { fontSize: metrics.glyphSize },
				})}
			>
				{item.icon ?? STATE_GLYPH[item.state]}
			</Box>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, flex: 1 }}>
				<Typography
					sx={{ fontSize: metrics.labelSize, fontWeight: metrics.labelWeight, lineHeight: metrics.labelLineHeight, color: labelColor(item.state, density) }}
				>
					{metrics.isolateLabel ? <bdi>{item.label}</bdi> : item.label}
				</Typography>
				{item.description && (
					<Typography component="div" sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary", lineHeight: 1.5 }}>
						{item.description}
					</Typography>
				)}
			</Box>
			{item.statusLabel && <MetaChip label={item.statusLabel} tone={STATE_CHIP_TONE[item.state]} size="small" />}
		</Box>
	);
};

/**
 * A **named list of things and where each one stands** — what a setup flow has written so far, which of
 * a feature's independent switches are on, what a confirm step is about to do.
 *
 * The counterpart to `SystemStageTrack`: the track is one thing moving through an ordered sequence, this
 * is several independent things each with their own state, where the order is presentation and not
 * progress. Reach for it instead of a hand-rolled `<CheckCircle/> + <Typography>` row stack, and instead
 * of a bulleted `SystemBanner` description when the rows carry state rather than just text.
 *
 * Fully prop-driven and copy-free: the caller owns every string (already translated) and decides what
 * each state means, exactly like `EmptyState`/`SystemBanner`. Glyph ink and chip tone resolve from the
 * theme palette, so the list re-tints with the brand.
 */
export const SystemStatusChecklist: FC<Props> = ({ items, title, stagger = true, ariaLabel, density = StatusChecklistDensity.Default, dataTestId, sx }) => {
	const metrics = DENSITY[density];
	return (
		<Box data-testid={dataTestId} sx={[{ display: "flex", flexDirection: "column", gap: 1 }, ...(Array.isArray(sx) ? sx : [sx])]}>
			{title && (
				<Typography
					component="h3"
					sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: LETTER_SPACING.LABEL, textTransform: "uppercase", color: "text.secondary" }}
				>
					{title}
				</Typography>
			)}
			<Box component="ul" aria-label={ariaLabel} sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: metrics.rowGap }}>
				{items.map((item, index) => (
					<StatusChecklistRow key={item.id} item={item} index={index} stagger={stagger} density={density} />
				))}
			</Box>
		</Box>
	);
};
